import { useState } from 'react';

import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ChevronDown,
  ClipboardList,
  Mail,
  MapPin,
  MessageCircle,
  MessageSquare,
  Phone,
  StickyNote,
} from 'lucide-react';

import { useActivityContext } from '@modules/_shared/components/feed/ActivityProvider';
import {
  quickActionButtonSx,
  tooltipProps,
} from '@modules/_shared/components/feed-v2/constants/styles';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useLocale } from '@shared/hooks/locale';

const menuItemSx = {
  py: 1,
  px: 1.5,
  '&:hover': {
    bgcolor: 'grey.50',
  },
} as const;

export const QuickActions = () => {
  const [messageAnchor, setMessageAnchor] = useState<HTMLElement | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const messageOpen = Boolean(messageAnchor);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { openDialog, openEmailDialog, phoneActions } = useActivityContext();
  const { t } = useLocale();

  const primaryActions = [
    { icon: StickyNote, label: t('Note'), action: () => openDialog('note') },
    { icon: ClipboardList, label: t('Task'), action: () => openDialog('task') },
    { icon: Phone, label: t('Call'), action: () => openDialog('call') },
    { icon: MapPin, label: t('Visit'), action: () => openDialog('visit') },
  ];

  const handlePhoneAction = (action: 'whatsapp' | 'sms') => {
    setMessageAnchor(null);

    if (!phoneActions?.canUsePhone) {
      setPhoneError(t('No phone number associated with this contact'));
      return;
    }

    if (action === 'whatsapp') {
      phoneActions.openWhatsApp?.();
    } else {
      phoneActions.openSMS?.();
    }
  };

  const messageOptions = [
    {
      icon: Mail,
      label: t('Email'),
      onClick: () => {
        setMessageAnchor(null);
        openEmailDialog?.();
      },
    },
    {
      icon: MessageCircle,
      label: t('WhatsApp'),
      onClick: () => handlePhoneAction('whatsapp'),
    },
    {
      icon: MessageSquare,
      label: t('SMS'),
      onClick: () => handlePhoneAction('sms'),
    },
  ];

  return (
    <>
      <Stack
        direction="row"
        spacing={{ xs: 0.5, sm: 1 }}
        sx={{ width: '100%', overflow: 'hidden' }}
      >
        {primaryActions.map(({ icon: Icon, label, action }) => (
          <Tooltip
            key={label}
            title={label}
            {...tooltipProps}
            disableHoverListener={!isMobile}
          >
            <Button
              variant="contained"
              onClick={action}
              sx={quickActionButtonSx}
            >
              <Box
                component="span"
                className="button-icon"
                sx={{ display: 'flex' }}
              >
                <Icon size={16} />
              </Box>
              <Box component="span" className="button-label">
                {label}
              </Box>
            </Button>
          </Tooltip>
        ))}

        <Tooltip
          title={t('Message')}
          {...tooltipProps}
          disableHoverListener={!isMobile}
        >
          <Button
            variant="contained"
            onClick={e => setMessageAnchor(e.currentTarget)}
            sx={quickActionButtonSx}
          >
            <Box
              component="span"
              className="button-icon"
              sx={{ display: 'flex' }}
            >
              <Mail size={16} />
            </Box>
            <Box component="span" className="button-label">
              {t('Message')}
            </Box>
            <ChevronDown
              size={12}
              style={{ marginLeft: 4, color: '#ffffff' }}
            />
          </Button>
        </Tooltip>

        <Menu
          anchorEl={messageAnchor}
          open={messageOpen}
          onClose={() => setMessageAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                minWidth: 160,
              },
            },
          }}
        >
          {messageOptions.map(({ icon: Icon, label, onClick }) => (
            <MenuItem key={label} onClick={onClick} sx={menuItemSx}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Icon
                  size={18}
                  style={{ color: (theme.vars ?? theme).palette.grey[600] }}
                />
              </ListItemIcon>
              <ListItemText
                primary={label}
                slotProps={{
                  primary: { variant: 'body2', sx: { fontWeight: 500 } },
                }}
              />
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      {phoneError && (
        <RASnackbar
          onClose={() => setPhoneError(null)}
          message={phoneError}
          severity="error"
        />
      )}
    </>
  );
};
